import { handleRequest } from "../../axios";
import { axiosInstance } from "./JsdhNotifikaceService";
import {
    ADMIN_DEVICES_URL,
    ADMIN_DEVICE_BY_ID_URL,
    ADMIN_DEVICE_PRINT_LOGS_URL,
    ADMIN_DEVICE_PRINT_QUEUE_URL,
    ADMIN_DEVICE_TEST_PRINT_URL,
    ADMIN_DEVICE_RETRY_PRINT_URL,
    ADMIN_DEVICE_PREVIEW_ALERT_URL,
    ADMIN_DEVICE_STOP_PREVIEW_ALERT_URL,
    ADMIN_DEVICE_PREVIEW_ALERT_STATUS_URL,
    ADMIN_DEVICE_SETUP_PACKAGE_URL,
} from "../url";
import type {
    DeviceDetail,
    DevicePrintLog,
    UpdateDeviceSettingsRequest,
    PrintQueueJob,
} from "../generated/models/Device";

// -----------------------------
// DEVICES
// -----------------------------

const getAllDevices = () => {
    return handleRequest<DeviceDetail[]>(() =>
        axiosInstance.get(ADMIN_DEVICES_URL)
    );
};

const getDeviceById = (id: number) => {
    return handleRequest<DeviceDetail>(() =>
        axiosInstance.get(ADMIN_DEVICE_BY_ID_URL(id))
    );
};

const updateDevice = (id: number, data: UpdateDeviceSettingsRequest) => {
    return handleRequest<DeviceDetail>(() =>
        axiosInstance.put(ADMIN_DEVICE_BY_ID_URL(id), data)
    );
};

const deleteDevice = (id: number) => {
    return handleRequest(() =>
        axiosInstance.delete(ADMIN_DEVICE_BY_ID_URL(id))
    );
};

const getDevicePrintLogs = (id: number, limit = 100) => {
    return handleRequest<DevicePrintLog[]>(() =>
        axiosInstance.get(ADMIN_DEVICE_PRINT_LOGS_URL(id), { params: { limit } })
    );
};

const getDevicePrintQueue = (id: number, limit = 50) => {
    return handleRequest<PrintQueueJob[]>(() =>
        axiosInstance.get(ADMIN_DEVICE_PRINT_QUEUE_URL(id), { params: { limit } })
    );
};

const testDevicePrint = (id: number) => {
    return handleRequest<{ success: boolean; message: string }>(() =>
        axiosInstance.post(ADMIN_DEVICE_TEST_PRINT_URL(id))
    );
};

const retryPrint = (deviceId: number, jobId: number) => {
    return handleRequest<{ success: boolean; message: string }>(() =>
        axiosInstance.post(ADMIN_DEVICE_RETRY_PRINT_URL(deviceId, jobId))
    );
};

// -----------------------------
// PREVIEW ALERT
// -----------------------------

export interface PreviewAlertResponse {
    success: boolean;
    message: string;
    durationSeconds?: number;
}

export interface PreviewAlertStatusResponse {
    active: boolean;
}

const getPreviewAlertStatus = (id: number) => {
    return handleRequest<PreviewAlertStatusResponse>(() =>
        axiosInstance.get(ADMIN_DEVICE_PREVIEW_ALERT_STATUS_URL(id))
    );
};

const startPreviewAlert = (id: number, durationSeconds = 60) => {
    return handleRequest<PreviewAlertResponse>(() =>
        axiosInstance.post(ADMIN_DEVICE_PREVIEW_ALERT_URL(id), null, {
            params: { durationSeconds },
        })
    );
};

const stopPreviewAlert = (id: number) => {
    return handleRequest<PreviewAlertResponse>(() =>
        axiosInstance.delete(ADMIN_DEVICE_STOP_PREVIEW_ALERT_URL(id))
    );
};

// -----------------------------
// SETUP PACKAGE
// -----------------------------

export interface SetupPackageRequest {
    profile: 'zigbee' | 'kiosk' | 'kiosk-zigbee';
    hostname: string;
    deviceName?: string;
    wifiSsid?: string;
    wifiPassword?: string;
    deviceId?: number;
}

const downloadSetupPackage = (data: SetupPackageRequest) => {
    return axiosInstance.post(ADMIN_DEVICE_SETUP_PACKAGE_URL, data, {
        responseType: 'blob',
    });
};

// -----------------------------

const DeviceService = {
    getAllDevices,
    getDeviceById,
    updateDevice,
    deleteDevice,
    getDevicePrintLogs,
    getDevicePrintQueue,
    testDevicePrint,
    retryPrint,
    getPreviewAlertStatus,
    startPreviewAlert,
    stopPreviewAlert,
    downloadSetupPackage,
};

export default DeviceService;
